import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useToast } from '../../context/ToastContext';
import { fetchAllBids, fetchAcceptedBids } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const bidStatusFilters = [
  { value: 'all', label: 'All Bids' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  expired: 'bg-gray-100 text-gray-700',
};

const bidExportColumns = [
  { key: 'id', label: 'Bid ID' },
  { key: 'event_title', label: 'Event' },
  { key: 'event_date', label: 'Event Date' },
  { key: 'caterer_name', label: 'Caterer' },
  { key: 'customer_email', label: 'Customer' },
  { key: 'amount', label: 'Amount' },
  { key: 'guests', label: 'Guests' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Created' },
];

export default function BidsPage() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [bids, setBids] = useState([]);
  const [acceptedBids, setAcceptedBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    let isMounted = true;

    async function loadBids() {
      setLoading(true);
      setError(null);

      try {
        if (activeTab === 'accepted') {
          const data = await fetchAcceptedBids(100);
          if (isMounted) {
            setAcceptedBids(data);
            setBids([]);
          }
        } else {
          const data = await fetchAllBids({ status: statusFilter === 'all' ? undefined : statusFilter });
          if (isMounted) {
            setBids(data);
            setAcceptedBids([]);
          }
        }
      } catch (err) {
        console.error('Failed to load bids', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load bids');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadBids();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, activeTab]);

  const displayBids = activeTab === 'accepted' ? acceptedBids : bids;
  const totalBidValue = displayBids.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const acceptedValue = acceptedBids.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Bid Management</h2>
          <p className="text-sm text-muted-foreground">
            View all bids, track accepted bids, and monitor bid activity across the platform.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const list = displayBids;
              const rows = list.map((b) => ({
                id: b.id,
                event_title: b.event?.title ?? b.event?.event_type ?? '',
                event_date: b.event?.event_date ? format(new Date(b.event.event_date), 'yyyy-MM-dd') : '',
                caterer_name: b.caterer?.business_name ?? '',
                customer_email: b.customer?.email ?? b.event?.customer_id ?? '',
                amount: b.amount,
                guests: b.event?.estimated_guests ?? '',
                status: b.status ?? '',
                created_at: b.created_at ? format(new Date(b.created_at), 'yyyy-MM-dd HH:mm') : '',
              }));
              if (!rows.length) {
                toast.info('No bids to export');
                return;
              }
              exportToCsv(rows, bidExportColumns, downloadFilename(activeTab === 'accepted' ? 'bids-accepted' : 'bids'));
              toast.success(`Exported ${rows.length} bids`);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Icon name="Download" size={16} /> Export
          </button>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Bids</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{bids.length}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name="FileText" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Accepted Bids</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-emerald-600">{acceptedBids.length}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <Icon name="CheckCircle" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Bid Value</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">R {totalBidValue.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <Icon name="DollarSign" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Accepted Value</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-emerald-600">R {acceptedValue.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <Icon name="TrendingUp" size={22} />
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          All Bids
        </button>
        <button
          onClick={() => setActiveTab('accepted')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'accepted'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Accepted Bids ({acceptedBids.length})
        </button>
      </div>

      {/* Bids Table */}
      <div className="rounded-xl border border-border bg-card shadow-sm">
        {activeTab === 'all' && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 px-4 py-4">
            {bidStatusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Bid ID</th>
                <th className="px-4 py-3 text-left font-semibold">Event</th>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Guests</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    Loading bids…
                  </td>
                </tr>
              )}

              {!loading && displayBids.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    No bids found.
                  </td>
                </tr>
              )}

              {displayBids.map((bid) => (
                <tr key={bid.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{bid.id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{bid.event?.event_type || bid.event?.title || 'Event'}</span>
                      <span className="text-xs text-muted-foreground">
                        {bid.event?.event_date ? format(new Date(bid.event.event_date), 'dd MMM yyyy') : '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{bid.caterer?.business_name || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{bid.customer?.email || bid.event?.customer_id?.slice(0, 8) || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R {Number(bid.amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-foreground">{bid.event?.estimated_guests || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      statusStyles[bid.status] ?? 'bg-muted text-foreground'
                    }`}>
                      {bid.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {bid.created_at ? format(new Date(bid.created_at), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted">
                      <Icon name="Eye" size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
